import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import * as categoriesService from '../../services/api/categories';
import * as languagesService from '../../services/api/languages';
import { Category } from '../../entities/category';
import { Language } from '../../entities/language';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import Heading1 from '../../components/common/headings/heading1';
import CategoriesFiltersComponent from '../../components/categories/filters';
import CategoriesTableComponent from '../../components/categories/table';
import CommonButtonDefault from '../../components/common/buttons/default';
import { strings } from '../../localization/strings';

const categoryTypes = [
  { id: 0, label: strings.categories.types.news },
  { id: 1, label: strings.categories.types.eltaGuide },
  { id: 2, label: strings.categories.types.expectedEvents },
];

function CategoriesListPageComponent() {
  const [queryParams, setQueryParams] = useSearchParams();
  const { token } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const selectedLanguage = parseInt(queryParams.get('lang') || '0', 10);
  const selectedType = parseInt(queryParams.get('type') || '0', 10);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const activeLanguages = await languagesService.list(token);

        setLanguages(activeLanguages);

        if (!queryParams.get('lang') && activeLanguages[0]) {
          const nextParams = new URLSearchParams(queryParams);

          nextParams.set('lang', activeLanguages[0].id.toString());
          nextParams.set('type', queryParams.get('type') || '0');
          setQueryParams(nextParams);
        }
      } catch (e) {
        setError((e as Error).message);
      }
    };

    fetchLanguages();
  }, [queryParams, setQueryParams, token]);

  useEffect(() => {
    if (!selectedLanguage) {
      return;
    }

    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        const response = await categoriesService.list(token, {
          language_id: selectedLanguage,
          type: selectedType,
          limit: -1,
          sort: 'type,sort_order',
        });

        setCategories(response.items);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchCategories();
  }, [selectedLanguage, selectedType, token]);

  const selectedLanguageData = useMemo(
    () => languages.find((language) => language.id === selectedLanguage),
    [languages, selectedLanguage],
  );

  const updateQuery = (key: string, value: number) => {
    const nextParams = new URLSearchParams(queryParams);

    nextParams.set(key, value.toString());
    setQueryParams(nextParams);
  };

  let categoryContent: React.ReactNode;

  if (isLoading) {
    categoryContent = <CommonAnimatedLoader />;
  } else if (categories.length > 0) {
    categoryContent = <CategoriesTableComponent categories={categories} />;
  } else {
    categoryContent = <div className="p-4 text-xl sm:p-6 lg:p-8">{strings.search.noResults}</div>;
  }

  return (
    <div>
      <header className="mb-3">
        <div className="flex justify-between space-x-3">
          <Heading1>{strings.categories.title}</Heading1>
          <Link to="/categories/create">
            <CommonButtonDefault type="button" primary>
              {strings.button.create}
            </CommonButtonDefault>
          </Link>
        </div>
      </header>
      <main className="w-full">
        <div className="overflow-hidden rounded-lg border border-gray-200 sm:rounded-lg">
          {error && <AlertPrimary type="danger" text={error} />}
          {languages.length > 0 && (
            <CategoriesFiltersComponent
              languages={languages}
              selectedLanguage={selectedLanguageData?.id || selectedLanguage}
              selectedType={selectedType}
              types={categoryTypes}
              onLanguageChange={(languageId) => updateQuery('lang', languageId)}
              onTypeChange={(type) => updateQuery('type', type)}
            />
          )}
          {categoryContent}
        </div>
      </main>
    </div>
  );
}

export default CategoriesListPageComponent;
